import * as React from "react";
import { StreamingContext, type StreamingClient } from "@authe/streaming";
import { sessionIdentityKey, tokenMatchesSession, type Session, type SessionManager } from "@authe/fe-commons/session";
import { useSession } from "@authe/ui-core/session";
import { ResourceChangeHub } from "@authe/fe-commons/events";
import { ResourceChangesProvider } from "@authe/ui-core/api";
import { resourceSyncConfig } from "@/config/resourceSync";
import { DashboardResourceSync } from "./DashboardResourceSync";
import { createDashboardStreamingClient } from "./streaming";
import { ServerStreamingContext, serverStreamingOf, useServerStreamingDecision } from "./serverStreaming";

/** Own the socket for exactly one authenticated identity, including impersonation. */
export function DashboardStreamingProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const { session, isAuthenticated, manager } = useSession();
  if (!isAuthenticated || !session) return <>{children}</>;
  const scope = sessionIdentityKey(session);
  return <AuthenticatedStreaming key={scope} manager={manager} session={session} scope={scope}>{children}</AuthenticatedStreaming>;
}

/**
 * Two lifetimes, deliberately separate:
 *
 *  - the IDENTITY's — token checks, pause/recover, logout. It owns the local
 *    `ResourceChangeHub`, which is cross-tab messaging inside this browser and
 *    runs on every plan;
 *  - the SERVER client's — which exists only while the identity is running AND
 *    `useServerStreamingDecision()` says connect. That is the one decision point
 *    for server streaming: a plan without `realtime_streaming` gets no client
 *    (and so no socket), a plan read still in flight gets none YET, and a plan
 *    that changes at runtime (a refetch after a refusal) creates or tears down
 *    the client without touching the hub.
 *
 * The client reaches the tree through `StreamingContext` directly rather than
 * `<StreamingProvider>`, so the tree's SHAPE never changes: swapping between
 * `<StreamingProvider>{content}</StreamingProvider>` and bare `content`
 * remounted the entire app beneath this component every time a client came or
 * went — which, now that a client waits for the plan read, would happen on
 * every page load. The client is started and stopped here instead.
 */
function AuthenticatedStreaming({ children, manager, session, scope }: {
  children: React.ReactNode;
  manager: SessionManager;
  session: Session;
  scope: string;
}): React.ReactElement {
  const decision = useServerStreamingDecision();
  const verdict = serverStreamingOf(decision);
  const [client, setClient] = React.useState<StreamingClient | null>(null);
  const [hub, setHub] = React.useState<ResourceChangeHub | null>(null);
  const expected = React.useRef(session);
  // The decision as the identity effect reads it, and that effect's way to act
  // on a change. Declared BEFORE the identity effect so the first run of that
  // effect already sees the first decision.
  const connectRef = React.useRef(decision.connect);
  const syncRef = React.useRef<(() => void) | null>(null);
  React.useEffect(() => {
    connectRef.current = decision.connect;
    syncRef.current?.();
  }, [decision.connect]);

  React.useEffect(() => {
    let active = true;
    let ended = false;
    let current: StreamingClient | null = null;
    let currentHub: ResourceChangeHub | null = null;
    let running = false;
    const stopClient = () => {
      const stopping = current;
      current = null;
      stopping?.stop();
      if (active) setClient(null);
    };
    const pause = () => {
      currentHub?.stop();
      currentHub = null;
      stopClient();
      running = false;
      if (active) setHub(null);
    };
    const end = () => { ended = true; pause(); };
    const openClient = () => {
      const created = createDashboardStreamingClient(async () => {
        const token = await manager.getAccessToken();
        if (!active || ended || created !== current) throw new Error("The streaming session ended");
        if (!token) {
          pause();
          throw new Error("The streaming token is unavailable");
        }
        if (!tokenMatchesSession(token, expected.current)) {
          end();
          throw new Error("The streaming session changed identity");
        }
        return token;
      }, scope, async () => {
        // Bypasses the SDK cache. Kept deliberately thin: the reconnect that
        // follows re-runs the guarded `getToken` above, so identity changes and
        // ended sessions are still caught in exactly one place.
        if (!active || ended) return null;
        return manager.refreshAccessToken().catch(() => null);
      });
      if (!created) return;
      current = created;
      created.start();
      setClient(created);
    };
    /** Bring the server client in line with (identity running) AND (decision says connect). */
    const sync = () => {
      if (!active || ended) return;
      const wanted = running && connectRef.current;
      if (wanted && !current) openClient();
      else if (!wanted && current) stopClient();
    };
    const start = () => {
      if (!active || ended || running) return;
      running = true;
      if (resourceSyncConfig.enabled) {
        currentHub = new ResourceChangeHub(scope, { ...resourceSyncConfig, namespace: "authe.dashboard.resource-changes.v1" });
        currentHub.start();
        setHub(currentHub);
      }
      sync();
    };
    syncRef.current = sync;
    const off = manager.subscribeIdentityChange(end);
    // A temporary refresh failure pauses delivery, then recovers with the same
    // identity. A logout or a foreign token ends this identity permanently.
    const check = async () => {
      const token = await manager.getAccessToken().catch(() => null);
      if (!active || ended) return;
      if (!token) pause();
      else if (!tokenMatchesSession(token, expected.current)) end();
      else start();
    };
    void check();
    const timer = setInterval(() => { void check(); }, 30_000);
    const storage = (event: StorageEvent) => {
      if (event.key === null || event.key.startsWith("@@auth0spajs@@")) void check();
    };
    window.addEventListener("storage", storage);
    return () => {
      active = false;
      ended = true;
      if (syncRef.current === sync) syncRef.current = null;
      off();
      clearInterval(timer);
      window.removeEventListener("storage", storage);
      currentHub?.stop();
      current?.stop();
    };
  }, [manager, scope]);

  return (
    <ServerStreamingContext.Provider value={verdict}>
      <ResourceChangesProvider hub={hub} revalidateOnFocus={resourceSyncConfig.revalidateOnFocus} revalidateOnOnline={resourceSyncConfig.revalidateOnOnline}>
        <StreamingContext.Provider value={client}>
          <DashboardResourceSync />
          {children}
        </StreamingContext.Provider>
      </ResourceChangesProvider>
    </ServerStreamingContext.Provider>
  );
}
